import posthog from "posthog-js";

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY || "phc_PLACEHOLDER";
const POSTHOG_HOST = "https://eu.i.posthog.com";
const CONSENT_KEY = "fl_consent";

let _initialised = false;

// Finding 8.D — consent gate. PostHog must not init or send any event
// until the user has explicitly accepted. `hasConsentChoice` tells the
// banner whether it needs to appear; `hasAcceptedAnalytics` tells the
// rest of the app whether to bother calling track functions.
export function hasConsentChoice() {
  const v = typeof window !== "undefined" && localStorage.getItem(CONSENT_KEY);
  return v === "accepted" || v === "rejected";
}

export function hasAcceptedAnalytics() {
  return typeof window !== "undefined" && localStorage.getItem(CONSENT_KEY) === "accepted";
}

export function initPostHog() {
  // Finding 8.D — do not init pre-consent. Called both from index.js on
  // boot (no-op unless user previously accepted) and from enableAnalytics
  // (after Accept click).
  if (!hasAcceptedAnalytics()) return;
  if (_initialised) return;
  // Finding 8.A — the build should already have failed via scripts/prebuild.js
  // if the key is missing/placeholder. This runtime check is defence-in-depth
  // for anyone who bypasses the prebuild (dev preview, local `craco build`,
  // etc.). Use console.error so it appears as a red row in the browser
  // devtools, not a soft warning.
  if (!POSTHOG_KEY || POSTHOG_KEY === "phc_PLACEHOLDER" || POSTHOG_KEY === "phc_REPLACE_WITH_YOUR_KEY") {
    console.error("[PostHog] REACT_APP_POSTHOG_KEY missing or placeholder — analytics disabled. This should never happen in production; check Netlify env config.");
    return;
  }
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    capture_pageview: true,
    capture_pageleave: true,
    // Finding 8.E — recordings mask all inputs AND all text. Insights,
    // diary, and patterns screens render personalised medical content
    // (condition-specific rating narratives, symptom summaries) as plain
    // DOM text, which would otherwise be captured verbatim by session
    // recording. maskAllText redacts that content while preserving
    // layout/click/timing signal so we still get UX visibility.
    session_recording: { maskAllInputs: true, maskAllText: true },
    autocapture: true,
  });
  _initialised = true;
}

// Called by the consent banner on Accept. Persists the choice, inits
// PostHog, and re-identifies the currently-signed-in user (if any) so
// they aren't left as an anonymous session.
export function enableAnalytics(user) {
  localStorage.setItem(CONSENT_KEY, "accepted");
  initPostHog();
  if (user && _initialised) identifyUser(user);
}

// ── Identity ──────────────────────────────────────────────────────────────────

export function identifyUser(user) {
  // Finding 8.D — no identity leak pre-consent.
  if (!user || !hasAcceptedAnalytics()) return;
  posthog.identify(user.id || user._id, {
    email: user.email,
    name: user.name,
    plan: user.is_premium ? "premium" : "free",
    conditions: (user.conditions || []).join(","),
    created_at: user.created_at,
  });
}

export function resetUser() {
  if (!hasAcceptedAnalytics()) return;
  posthog.reset();
}

// ── Helper ────────────────────────────────────────────────────────────────────

function track(event, props = {}) {
  // Finding 8.D — no events pre-consent.
  if (!hasAcceptedAnalytics()) return;
  try {
    posthog.capture(event, props);
  } catch (e) {
    // Never throw from analytics
  }
}

// ── Auth & Onboarding ─────────────────────────────────────────────────────────

export const ph = {
  // Auth
  userSignedUp: (user) =>
    track("user_signed_up", {
      email: user.email,
      conditions: (user.conditions || []).join(","),
    }),
  userLoggedIn: () => track("user_logged_in"),
  userLoggedOut: () => track("user_logged_out"),
  userDeletedAccount: () => track("user_deleted_account"),
  passwordResetRequested: () => track("password_reset_requested"),

  // Onboarding
  onboardingStepViewed: (step_name, step_number) =>
    track("onboarding_step_viewed", { step_name, step_number }),
  onboardingCompleted: (conditions, goals) =>
    track("onboarding_completed", {
      conditions: (conditions || []).join(","),
      goals: (goals || []).join(","),
    }),
  conditionSelected: (condition) =>
    track("condition_selected", { condition }),

  // Food scanning & search
  barcodeScannerOpened: () => track("barcode_scanner_opened"),
  barcodeScanned: (food_name, barcode) =>
    track("barcode_scanned", { food_name, barcode }),
  barcodeScanFailed: (reason) =>
    track("barcode_scan_failed", { reason }),
  barcodeProviderDown: () => track("barcode_provider_down"),
  foodSearched: (query) => track("food_searched", { query }),
  foodNotFound: (query) => track("food_not_found", { query }),
  manualFoodEntryStarted: (barcode) =>
    track("manual_food_entry_started", { barcode }),

  // Ratings & results
  ratingViewed: (rating) =>
    track("rating_viewed", {
      food_name: rating.food_name || rating.name,
      overall_score: rating.overallScore,
      naturalness: rating.dimensions?.naturalness?.score,
      hormonal_impact: rating.dimensions?.hormonalImpact?.score,
      inflammation: rating.dimensions?.inflammation?.score,
      gut_health: rating.dimensions?.gutHealth?.score,
    }),
  ratingSaved: (food_name, overall_score) =>
    track("rating_saved", { food_name, overall_score }),
  ratingShared: (food_name, overall_score) =>
    track("rating_shared", { food_name, overall_score }),
  ingredientDetailExpanded: (food_name, dimension) =>
    track("ingredient_detail_expanded", { food_name, dimension }),
  recommendationClicked: (food_name, alternative_name) =>
    track("recommendation_clicked", { food_name, alternative_name }),
  quickPickClicked: (food_name) =>
    track("quick_pick_clicked", { food_name }),
  favouriteToggled: (food_name, saved) =>
    track("favourite_toggled", { food_name, saved }),

  // Diary
  diaryOpened: () => track("diary_opened"),
  diaryEntryAdded: (food_name, overall_score) =>
    track("diary_entry_added", { food_name, overall_score }),
  diaryEntryDeleted: (food_name) =>
    track("diary_entry_deleted", { food_name }),
  diaryDateChanged: (date) => track("diary_date_changed", { date }),
  diaryLockedHit: () => track("diary_locked_hit"),

  // Symptom tracker
  symptomCheckinOpened: () => track("symptom_checkin_opened"),
  symptomCheckinCompleted: (scores) =>
    track("symptom_checkin_completed", scores),

  // Meal planner
  mealPlannerOpened: () => track("meal_planner_opened"),
  mealPlannerMealRated: (meal_name) =>
    track("meal_planner_meal_rated", { meal_name }),

  // My Foods
  myFoodsTabChanged: (tab) => track("my_foods_tab_changed", { tab }),
  historyItemRerated: (food_name) =>
    track("history_item_rerated", { food_name }),
  shoppingItemAdded: (name) =>
    track("shopping_item_added", { name }),
  shoppingItemChecked: (item_id) =>
    track("shopping_item_checked", { item_id }),

  // Paywall & subscription
  paywallHit: (trigger) => track("paywall_hit", { trigger }),
  upgradeModalViewed: (trigger) =>
    track("upgrade_modal_viewed", { trigger }),
  upgradeCTAClicked: (plan) =>
    track("upgrade_cta_clicked", { plan }),
  freeTrialStarted: (plan) =>
    track("free_trial_started", { plan }),
  referralLinkCopied: () => track("referral_link_copied"),
  referralLinkShared: () => track("referral_link_shared"),
  manageSubscriptionClicked: () => track("manage_subscription_clicked"),
  subscriptionCancelled: () => track("subscription_cancelled"),

  // Navigation
  tabChanged: (tab_name) => track("tab_changed", { tab_name }),
  profileViewed: () => track("profile_viewed"),
  insightsViewed: () => track("insights_viewed"),
  insightsSectionChanged: (section) =>
    track("insights_section_changed", { section }),

  // Errors
  apiError: (endpoint, error_message, status) =>
    track("api_error", { endpoint, error_message, status }),
  scanLimitReached: () => track("scan_limit_reached"),
  networkError: (context) => track("network_error", { context }),
};
